import { localRuntimeServerSchema, type ServerProfile } from '@crowdex/agent-contracts/api';
import { apiFetch } from '@crowdex/host-kit/renderer/http';

/**
 * What the server this renderer talks to can do, learned from
 * `/api/app/bootstrap`. The local profile (`crowdex serve`) runs on the user's
 * machine and exposes its workspace and agent server; the cloud profile does
 * not. Electron-only capabilities are a separate question: see
 * `isElectronDesktop()`.
 */

export type LocalWorkspaceServer = { url: string; token: string };
export type LocalWorkspaceServerStatus =
  | { state: 'starting' }
  | { state: 'ready'; server: LocalWorkspaceServer }
  | { state: 'failed'; message: string };

type Listener = (status: LocalWorkspaceServerStatus) => void;

let profile: ServerProfile = 'cloud';
let status: LocalWorkspaceServerStatus | null = null;
let connecting: Promise<void> | null = null;
const listeners = new Set<Listener>();

function publish(next: LocalWorkspaceServerStatus): void {
  status = next;
  for (const listener of listeners) listener(next);
}

/** Records the profile reported by the bootstrap and, locally, fetches the workspace server. */
export function setServerProfile(next: ServerProfile): Promise<void> {
  profile = next;
  if (next !== 'local') return Promise.resolve();
  return connectLocalWorkspaceServer();
}

export function serverProfile(): ServerProfile {
  return profile;
}

/** True when the server runs on this machine: local folders, Git, terminals and agents. */
export function isLocalMachine(): boolean {
  return profile === 'local';
}

/** (Re)reads the in-process workspace server's address from the local server. */
export function connectLocalWorkspaceServer(): Promise<void> {
  if (connecting !== null) return connecting;
  if (status?.state !== 'ready') publish({ state: 'starting' });
  connecting = apiFetch<unknown>('/api/local/runtime', { credentials: 'same-origin' })
    .then((data) => {
      const parsed = localRuntimeServerSchema.safeParse(data);
      if (!parsed.success) throw new Error('The local server returned an invalid runtime.');
      // The server returns a path on its own origin; sockets need an absolute ws(s) URL.
      const url = new URL(parsed.data.url, window.location.href);
      url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      publish({ state: 'ready', server: { url: url.toString(), token: parsed.data.token } });
    })
    .catch((reason: unknown) => {
      publish({
        state: 'failed',
        message: reason instanceof Error ? reason.message : String(reason),
      });
    })
    .finally(() => {
      connecting = null;
    });
  return connecting;
}

/** The local workspace server, or `null` while it is unknown or outside the local profile. */
export function localWorkspaceServer(): LocalWorkspaceServer | null {
  return status?.state === 'ready' ? status.server : null;
}

/** Current workspace-server state. `null` only means this is not the local profile. */
export function localWorkspaceServerStatus(): LocalWorkspaceServerStatus | null {
  return profile === 'local' ? (status ?? { state: 'starting' }) : null;
}

export function subscribeLocalWorkspaceServerStatus(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** An HTTP URL of the local workspace server (its routes are `/v1/*`). */
export function localServerUrl(server: LocalWorkspaceServer, path: string): string {
  const url = new URL(server.url);
  url.protocol = url.protocol === 'wss:' ? 'https:' : 'http:';
  url.pathname = path;
  url.search = '';
  url.hash = '';
  return url.toString();
}
